'use strict';

import * as util from '../util/Common.js';
import {EventManager} from '../util/EventManager.js';
import {Point2D} from '../util/Geometry.js';
import * as domUtil from '../util/Dom.js';
import {UiComponent} from '../ui/UiComponent.js';
import {RelativeContainer} from '../ui/RelativeContainer.js';
import {SequenceController} from '../viewer/SequenceView.js';

const privateMap = util.createPrivateMapper();

export class Viewer extends EventManager {
  constructor(options){
    super();
    const pv = privateMap(this);
    pv.contentView = options.contentView
    pv.sequenceViews = Object.assign({}, options.sequenceViews);
    pv.sequenceComponents = options.sequenceComponents ? [...options.sequenceComponents] : [];
    pv.manifestMetadataView = options.manifestMetadataView;
    pv.tocView = options.tocView;
    pv.uiComponents = options.uiComponents ? [...options.uiComponents] : [];
    pv.sequenceControllers = [];
    pv.currentSequenceNo = -1;
    pv.currentViewTypes = {};
    pv.currentViewType = null;
    pv.currentSequenceView = null;
    pv.currentSequenceController = null;
    pv.currentSceneMap = {};
    pv.currentTocMap = {};
  }

  reset(){
    const pv = privateMap(this);
    this.dispatchEvent('reset');
    if (pv.manifestMetadataView) {
      pv.manifestMetadataView.metadata = null;
    }
    util.each(pv.sequenceComponents, (sc) => {
      sc.controller = null;
    });
    util.each(pv.uiComponents, (uc) => {
      uc.active = false;
    });
    if (pv.currentSequenceController){
      pv.currentSequenceController.removeAllListeners();
      pv.currentSequenceController = null;
    }
    if (pv.currentSequenceView) {
      pv.currentSequenceView.controller = null;
      pv.currentSequenceView = null;
    }
    if (pv.tocView) {
      pv.tocView.structures = null;
    }
    pv.currentViewType = null;
    pv.currentSequenceNo = -1;
    pv.currentViewTypes = {};
    pv.currentSceneMap = {};
    pv.currentTocMap = {};
  }

  get manifest(){
    return privateMap(this).manifest;
  }
  set manifest(manifest){
    const pv = privateMap(this);

    pv.manifest = manifest;
    if (!pv.manifest) {
      this.reset();
      return
    }
    util.each(pv.uiComponents, (uc) => {
      uc.active = true;
    });

    if (pv.manifestMetadataView) {
      pv.manifestMetadataView.metadata = manifest.metadata;
    }
    if (pv.tocView) {
      pv.tocView.structures = manifest.structures;
    }
    pv.sequenceControllers.length = 0;
    pv.currentSequenceNo = -1;
    util.each(manifest.sequences, (sequence, seqIdx) => {
      const sequenceController = new SequenceController(sequence);
      const validViewTypes = [];
      util.each(sequence.viewTypes, (vt) => {
        if (!pv.sequenceViews[vt]) {
          console.error('sequence view type invalid. type:' + vt);
          return;
        }
        validViewTypes.push(vt);
      });
      if (validViewTypes.length == 0) {
        console.error('sequence view type unavailable. sequence:' + seqIdx);
        return;
      }
      pv.sequenceControllers.push(sequenceController);
      util.each(sequence.scenes, (scene, sceneIdx) => {
        pv.currentSceneMap[scene.id] = {
          sequenceNo: seqIdx,
          sceneNo: sceneIdx
        };
      })
    });
    util.each(manifest.structures, (structure) => {
      const sceneIds = structure.data.sceneIds;
      if (!sceneIds || !sceneIds.length) {
        return;
      }
      util.each(sceneIds, (sceneId) => {
        const sceneRef = pv.currentSceneMap[sceneId];
        if (!sceneRef) {
          return;
        }
        const key = sceneRef.sequenceNo + '_' + sceneRef.sceneNo;
        pv.currentTocMap[key] = pv.currentTocMap[key] ? pv.currentTocMap[key] : [];
        pv.currentTocMap[key].push(structure.id);
      })
    })
  }
  get currentSequenceController(){
    return privateMap(this).currentSequenceController;
  }
  get sequenceLength(){
    return privateMap(this).sequenceControllers.length;
  }
  get sequenceNo(){
    return privateMap(this).currentSequenceNo;
  }
  set sceneId(sceneId){
    const pv = privateMap(this);
    const sceneRef = pv.currentSceneMap[sceneId];
    if (!sceneRef) {
      return;
    }
    let seqChanged = false;
    if (pv.currentSequenceNo != sceneRef.sequenceNo) {
      this.sequenceNo = sceneRef.sequenceNo;
      seqChanged = true;
    }
    if (!pv.currentSequenceController) {
      return;
    }
    if (seqChanged || sceneRef.sceneNo != pv.currentSequenceController.sceneNo) {
      pv.currentSequenceController.sceneNo = sceneRef.sceneNo;
    }
  }
  set sequenceNo(no){
    const pv = privateMap(this);
    const sequenceNo = Math.min(pv.sequenceControllers.length - 1, Math.max(no, 0));
    if (pv.currentSequenceNo == sequenceNo) {
      // no change
      return;
    }
    const sequenceController = pv.sequenceControllers[sequenceNo];
    pv.currentSequenceNo = -1;

    // const sequenceView = pv.sequenceViews[sequenceController.viewType];
    this.viewType = null;
    if (pv.currentSequenceController && pv.currentSequenceController != sequenceController) {
      pv.currentSequenceController.removeAllListeners();
      // pv.currentSequenceView.controller = null;
      util.each(pv.sequenceComponents, (component) => {
        component.controller = null;
      });
    }

    // pv.currentSequenceView = sequenceView;
    pv.currentSequenceController = sequenceController;
    pv.currentViewTypes = {};
    if (pv.currentSequenceController) {
      pv.currentSequenceNo = sequenceNo;
      pv.currentSequenceController.addEventListener('viewchange', (ev) => {
        const key = pv.currentSequenceNo + '_' + ev.detail;
        const tocs = pv.currentTocMap[key] ? pv.currentTocMap[key] : [];
        if (pv.tocView) {
          pv.tocView.selectIds(tocs);
        }
        this.dispatchEvent('viewchange', ev);
      });
      pv.currentSequenceController.addEventListener('scenechange', (ev) => {
        this.dispatchEvent('scenechange', ev);
      });
        // sequenceView.controller = sequenceController;
      util.each(pv.sequenceComponents, (component) => {
        component.controller = sequenceController;
      });
      util.each(sequenceController.viewTypes, (vt) => {
        const sequenceView = pv.sequenceViews[vt];
        if (!sequenceView) {
          return;
        }
        pv.currentViewTypes[vt] = sequenceView;
      });
    }
    this.dispatchEvent('sequencechange');
    if (pv.currentSequenceController) {
      this.dispatchEvent('viewchange', {
        detail: pv.currentSequenceController.sceneNo
      });
    }
  }
  
  get currentViewTypes(){
    const pv = privateMap(this);
    const viewTypes = [];
    for (let k in pv.currentViewTypes) {
      viewTypes.push(k);
    }
    return viewTypes;
  }
  get viewType(){
    return privateMap(this).currentViewType;
  }
  set viewType(viewType) {
    const pv = privateMap(this);
    if (!pv.currentSequenceController) {
      // sequence not selected
      return;
    }
    if (pv.currentViewType == viewType) {
      // no change
      return;
    }
    pv.contentView.reset()
    pv.currentViewType = viewType;
    if (!viewType || !viewType.length) {
      // unset view
      if (pv.currentSequenceView) {
        pv.currentSequenceView.unlink()
        pv.currentSequenceView.controller = null;
      }
      return;
    }
    const sequenceView = pv.currentViewTypes[viewType];
    if (pv.currentSequenceView && pv.currentSequenceView != sequenceView) {
      pv.currentSequenceView.unlink()
      pv.currentSequenceView.controller = null;
    }
    if (!sequenceView) {
      console.error('invalid sequence view type. view type:' + viewType);
    }
    pv.currentSequenceView = sequenceView;
    if (sequenceView) {
      pv.currentSequenceView.link(pv.contentView)
      pv.currentSequenceView.controller = pv.currentSequenceController;
    }
    this.dispatchEvent('viewtypechange');
  }

  getSequenceController(no){
    return privateMap(this).sequenceControllers[no];
  }
  eachSequenceController(fn){
    const pv = privateMap(this);
    util.each(pv.sequenceControllers, fn);
  }
}